import { useState, useContext, createContext } from "react";
import { X } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../components/Style/EnquiryModal.css";
import { useLeadForm } from "../hooks/useLeadForm";
import { Link, useNavigate } from "react-router-dom";

const EnquiryModalContext = createContext();
export const useEnquiryModal = () => useContext(EnquiryModalContext);

export const EnquiryModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [projectData, setProjectData] = useState(null);
  // const openModal = () => setIsOpen(true);
  const openModal = (data = null) => {
    setProjectData(data); // 👈 project title store
    setIsOpen(true);
  };
  // const closeModal = () => setIsOpen(false);
  const closeModal = () => {
    setProjectData(null);
    setIsOpen(false);
  };

  return (
    <EnquiryModalContext.Provider value={{ openModal, closeModal, projectData }}>
      {children}
      {isOpen && <EnquiryModal onClose={closeModal} projectData={projectData} />}
    </EnquiryModalContext.Provider>
  );
};

const EnquiryModal = ({ onClose, projectData }) => {
  const navigate = useNavigate();
  const handleClose = () => {
    const goBack = sessionStorage.getItem("goBackAfterClose");

    onClose();

    if (goBack === "true") {
      sessionStorage.removeItem("goBackAfterClose");
      navigate(-1); // ✅ ONLY go back when opened from mobile menu
    }
  };
  // const projectName = projectData?.project_name || "Nova Capital Real Estate";

  const {
    phone,
    setPhone,
    dialCode,
    isoCode,
    loading,
    submitLead,
    setDialCode,
    setIsoCode,
    setNumberWithoutCountryCode,
  } = useLeadForm();

  const [consentChecked, setConsentChecked] = useState(true);
  const [errors, setErrors] = useState({ name: "", phone: "", consent: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = e.target.name.value.trim();
    const email = e.target.email.value.trim();
    // const projectName = localStorage.getItem("selected_project_name") || "Nova Capital Real Estate";
    const projectName = projectData?.projectTitle || "Alef Group UAE";
    const message = e.target.message.value.trim();
    const cleanedPhone = phone ? phone.replace(/\D/g, "") : "";

    const newErrors = { name: "", phone: "", email: "", message: "" };

    // ✅ Name validation
    if (!name) {
      newErrors.name = "Name is required";
    }

    if (!email) {
      newErrors.email = "Email is required";
    }

    // ✅ Phone validation (strong)
    if (!cleanedPhone) {
      newErrors.phone = "Phone number is required";
    }
    // Allow country code but ensure at least 8 digits total (including code)
    else if (cleanedPhone.length < 8) {
      newErrors.phone = "Please enter a valid phone number";
    }

    // ✅ Consent validation
    if (!message) {
      newErrors.message = "Message is required";
    }

    // Stop form if errors exist
    if (newErrors.name || newErrors.phone || newErrors.email || newErrors.message) {
      setErrors(newErrors);
      return;
    }

    // ✅ No errors — clear errors and submit
    setErrors({ name: "", phone: "", email: "", message: ""});
    const formData = { name, email, project_name: projectName, message: e.target.message.value.trim()};

    const result = await submitLead(formData, e.target);
    if (result) {
      e.target.reset();
      setConsentChecked(false);
      onClose();
      navigate("/thankyou.html");
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-white rounded-2xl w-full max-w-lg mx-4 flex flex-col md:flex-row relative shadow-2xl overflow-hidden">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 text-gray-700 hover:text-black z-20"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Left: Form */}
          <div className="w-full p-8 text-black">
            <h2 className="text-2xl font-bold mb-2">
              Get Expert Advice on Alef Group
            </h2>
            <p className="mb-4 text-sm">
              Whether you're exploring investment opportunities or searching for your dream home, our Alef Group specialists are here to guide you every step of the way.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4 text-black">
              <div className="">
                <h2 className="text-md mb-2 text-[#000000] font-semibold">Name</h2>
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  className="w-full px-4 py-2 rounded-md border border-sky-600 focus:ring-1 focus:ring-sky-600 outline-none"
                  required
                />
                {errors.name && (
                  <span className="text-black text-sm mt-1">{errors.name}</span>
                )}
              </div>

              <div className="">
                <h2 className="text-md mb-2 text-[#000000] font-semibold">Email</h2>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  className="w-full px-4 py-2 rounded-md border border-sky-600 focus:ring-1 focus:ring-sky-600 outline-none"
                  required
                />
                {errors.email && (
                  <span className="text-black text-sm mt-1">{errors.email}</span>
                )}
              </div>

              <div className="">
                <h2 className="text-md mb-2 text-[#000000] font-semibold">Phone</h2>
                <PhoneInput
                  country={isoCode || "in"}
                  value={phone}
                  onChange={(value, country) => {
                    setPhone(value);
                    setDialCode(`${country.dialCode}`);
                    setIsoCode(country.countryCode);
                    setNumberWithoutCountryCode(
                      value.replace(country.dialCode, "")
                    );
                  }}
                  inputProps={{ name: "phone", required: true, id: "phone" }}
                  containerClass="w-full"
                  inputClass="!w-full !py-3 !pl-12 !pr-3 !rounded-md !border !border-sky-600 focus:!border-sky-600 !outline-none"
                  buttonClass="!bg-gray-200 !border-none !rounded-l-md"
                  dropdownClass="!bg-black !text-white"
                  enableSearch
                />
                {errors.phone && (
                  <span className="text-black text-sm mt-1">{errors.phone}</span>
                )}
              </div>

              <div className="">
                <h2 className="text-md mb-2 text-[#000000] font-semibold">Message</h2>
                <textarea
                  name="message"
                  placeholder="Your Message"
                  className="w-full px-4 py-2 rounded-md border border-sky-600 focus:ring-1 focus:ring-sky-600 outline-none"
                  required
                />
                {errors.message && (
                  <span className="text-black text-sm mt-1">{errors.message}</span>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-sky-600 hover:bg-[#ffffff] border-2 border-sky-600 hover:text-sky-600 text-white font-semibold py-3 rounded-md transition"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Enquiry"}
              </button>
            </form>
          </div>

          {/* Right: Image (hidden on mobile) */}
          {/* <div className="hidden md:flex w-1/2 bg-[#f8fbff] border justify-center items-center">
            <img
              src="../Assets/enquiry-agent.jpg"
              alt="Agent"
              className="object-cover w-full h-full"
            />
          </div> */}
        </div>
      </div>
    </>
  );
};

export default EnquiryModal;
